<template>
  <!--
    ── Lo que recuerdo de ti ─────────────────────────────────────────────────
    Lo que la persona confirmó que el asistente recuerde —preferencias, sus
    nombres para las cosas, datos de su trabajo— y los nombres de la empresa,
    que ve todo el mundo y solo borra quien administra. Borrar pregunta antes:
    no tiene vuelta. Dianas de 44 px e iconos de trazo, como el resto del panel.
  -->
  <div class="memories">
    <p class="memories__intro">{{ t('assistant.memory.intro') }}</p>

    <div v-if="loading" class="memories__list">
      <UiSkeleton v-for="n in 3" :key="n" height="52px" />
    </div>
    <p v-else-if="store.memoriesFailed" class="memories__muted" role="alert">{{ t('assistant.memory.failed') }}</p>

    <template v-else>
      <p v-if="!store.memories.length" class="memories__muted">{{ t('assistant.memory.empty') }}</p>

      <section v-if="own.length" class="memories__group" :aria-label="t('assistant.memory.mine')">
        <h3 class="memories__heading">{{ t('assistant.memory.mine') }}</h3>
        <ul class="memories__list">
          <li v-for="memory in own" :key="memory.id" class="memory">
            <span class="memory__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path :d="iconOf(memory.kind)" /></svg>
            </span>
            <span class="memory__body">
              <span class="memory__text">{{ textOf(memory) }}</span>
              <span class="memory__meta">{{ t(`assistant.memory.kind.${memory.kind}`) }}<template v-if="memory.createdAt"> · {{ formatRelative(memory.createdAt) }}</template></span>
            </span>
            <button type="button" class="memory__delete" :aria-label="t('assistant.memory.forget')" :title="t('assistant.memory.forget')" @click="forget(memory)">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" /></svg>
            </button>
          </li>
        </ul>
      </section>

      <section v-if="shared.length" class="memories__group" :aria-label="t('assistant.memory.company')">
        <h3 class="memories__heading">{{ t('assistant.memory.company') }}</h3>
        <p class="memories__muted memories__note">{{ store.canShareMemories ? t('assistant.memory.companyAdmin') : t('assistant.memory.companyReadOnly') }}</p>
        <ul class="memories__list">
          <li v-for="memory in shared" :key="memory.id" class="memory">
            <span class="memory__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path :d="iconOf(memory.kind)" /></svg>
            </span>
            <span class="memory__body">
              <span class="memory__text">{{ textOf(memory) }}</span>
              <span class="memory__meta">{{ t(`assistant.memory.kind.${memory.kind}`) }}</span>
            </span>
            <button v-if="store.canShareMemories" type="button" class="memory__delete" :aria-label="t('assistant.memory.forget')" :title="t('assistant.memory.forget')" @click="forget(memory)">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" /></svg>
            </button>
          </li>
        </ul>
      </section>

      <div v-if="own.length" class="memories__clear">
        <UiButton size="sm" variant="ghost" @click="forgetAll">{{ t('assistant.memory.forgetAll') }}</UiButton>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiButton, UiSkeleton, useConfirm, useToast, formatRelative } from '@general/ui-kit'

import { useAssistantStore } from '../store'

const { t } = useI18n()
const store = useAssistantStore()
const { confirm } = useConfirm()
const toast = useToast()

const loading = ref(true)
const own = computed(() => store.memories.filter(memory => memory.scope !== 'company'))
const shared = computed(() => store.memories.filter(memory => memory.scope === 'company'))

// Una preferencia es un ajuste, un nombre una etiqueta, un dato una nota.
const ICONS = {
  preference: 'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6',
  alias: 'M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8zM7 7h.01',
  fact: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5',
}
const iconOf = (kind) => ICONS[kind] ?? ICONS.fact

/** «"mi cuadrilla" → Marta y Marc» para un nombre; el texto tal cual para lo demás. */
function textOf(memory) {
  return memory.kind === 'alias' && memory.alias
    ? t('assistant.memory.aliasLine', { alias: memory.alias, text: memory.text })
    : memory.text
}

async function forget(memory) {
  const yes = await confirm({
    title: t('assistant.memory.forgetTitle'),
    message: t('assistant.memory.forgetMessage', { text: textOf(memory) }),
    confirmLabel: t('assistant.memory.forget'),
    cancelLabel: t('common.cancel'),
    danger: true,
  })
  if (!yes) return
  try {
    await store.forgetMemory(memory.id)
    toast.success(t('assistant.memory.forgotten'))
  } catch {
    toast.error(t('assistant.memory.failed'))
  }
}

async function forgetAll() {
  const yes = await confirm({
    title: t('assistant.memory.forgetAllTitle'),
    message: t('assistant.memory.forgetAllMessage'),
    confirmLabel: t('assistant.memory.forgetAll'),
    cancelLabel: t('common.cancel'),
    danger: true,
  })
  if (!yes) return
  try {
    await store.forgetAllMemories()
    toast.success(t('assistant.memory.forgottenAll'))
  } catch {
    toast.error(t('assistant.memory.failed'))
  }
}

onMounted(async () => {
  await store.loadMemories()
  loading.value = false
})
</script>

<style scoped>
.memories { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-4); }
.memories__intro { margin: 0; color: var(--color-text-soft); font-size: var(--text-sm); }
.memories__muted { margin: 0; color: var(--color-text-mute); font-size: var(--text-sm); }
.memories__note { font-size: var(--text-xs); }
.memories__group { display: flex; flex-direction: column; gap: 6px; }
.memories__heading { margin: 0; font-size: var(--text-xs); font-weight: 600; color: var(--color-text-mute); text-transform: uppercase; letter-spacing: .04em; }
.memories__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.memories__clear { display: flex; justify-content: center; padding-top: var(--space-2); }

.memory {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: 52px;
  padding: 4px 4px 4px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
}
.memory__icon { flex: none; display: inline-flex; color: var(--color-text-mute); }
.memory__icon svg,
.memory__delete svg {
  width: 20px; height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.memory__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.memory__text { overflow-wrap: anywhere; }
.memory__meta { font-size: var(--text-xs); color: var(--color-text-mute); }
.memory__delete {
  flex: none;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-full);
  background: none;
  color: var(--color-text-soft);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.memory__delete:hover { background: var(--color-danger-soft, var(--color-gray-100)); color: var(--color-danger); }
.memory__delete:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 1px; }
</style>
